/**
 * My Rentals (§7.1) — the renter dashboard. Same shape as My Bots because it is
 * the same kind of thing: one side of a per-bot relationship.
 */

import { Link } from 'react-router-dom';
import { EMPTY_STATES } from '@pm/shared';

import { useApi } from '../../hooks/useApi';
import type { Page, Rental } from '../../api/types';
import { Card, Loading, Badge } from '../../components/ui';
import { EmptyState } from '../../components/EmptyState';
import { ErrorView } from '../../components/ErrorView';
import { amountLabel, duration, humanize, localDateTime } from '../../lib/format';
import { DECLARED_LABEL, OWNER_LABEL } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

function tone(status: Rental['status']) {
  if (status === 'active') return 'ok' as const;
  if (status === 'requested' || status === 'scheduled') return 'info' as const;
  if (status.startsWith('terminated') || status.startsWith('cancelled') || status === 'declined')
    return 'bad' as const;
  return 'neutral' as const;
}

export function MyRentals() {
  const rentals = useApi<Page<Rental>>(routes.rentals.collection, {
    query: { role: 'renter', limit: 50 },
    pollMs: 60_000,
  });

  if (rentals.initialLoading) return <Loading label="Loading your rentals" />;
  if (rentals.problem) return <ErrorView problem={rentals.problem} onRetry={rentals.reload} />;

  const list = rentals.data?.data ?? [];

  return (
    <div className="my-rentals">
      <header className="page-head">
        <h1>My Rentals</h1>
      </header>

      {list.length === 0 ? (
        <Card>
          <EmptyState
            heading={EMPTY_STATES.renterNoRentals.heading}
            body={EMPTY_STATES.renterNoRentals.body}
            cta={{ label: EMPTY_STATES.renterNoRentals.cta, to: '/browse' }}
          />
        </Card>
      ) : (
        <ul className="rental-list">
          {list.map((r) => (
            <li key={r.id} className="rental-card">
              <div className="rental-card-head">
                <Link to={`/rentals/${r.id}/console`}>
                  <h2>{r.bot_name}</h2>
                </Link>
                <Badge tone={tone(r.status)}>{humanize(r.status)}</Badge>
              </div>
              <p className="muted">
                {OWNER_LABEL} @{r.owner_handle} · {r.platform}
              </p>
              <p>
                {localDateTime(r.start_at)} – {localDateTime(r.end_at)}
                {r.status === 'active' ? (
                  <>
                    {' · '}
                    {duration(Math.max(0, (new Date(r.end_at).getTime() - Date.now()) / 1000))} left
                  </>
                ) : null}
              </p>
              <p className="muted">
                {DECLARED_LABEL}: {amountLabel(r.declared)}, arranged with the owner.
              </p>

              {r.pending_consent ? (
                <Link className="btn btn-primary btn-sm" to={`/rentals/${r.id}/consent`}>
                  A change is waiting on you
                </Link>
              ) : null}
              {r.review?.can_submit ? (
                <Link className="btn btn-sm" to={`/rentals/${r.id}/review`}>
                  Leave a review
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
